'use client';

import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import { Product as DisplayProduct } from '@wconcept/wconcept-common/models/display/product';
import {
  getCookieTrackingIssueNo,
  getCookieWckAuthData,
  getWckRecentResultList,
} from '@wconcept/wconcept-common/utils/cookie';
import {
  addAsIsMWebDomain,
  addAsIsPcWebDomain,
  currentUrl,
} from '@utils/domainUtil';
import { isNativeAppBoth } from '@utils/userAgentUtil';

import { useCallback } from 'react';
import {
  MyHeartBrandRequestParam,
  MyHeartContentRequestParam,
  MyHeartItemRequestParam,
  requestAddMyHeartBrand,
  requestAddMyHeartContents,
  requestAddMyHeartItem,
  requestDeleteMyHeartBrand,
  requestDeleteMyHeartContents,
  requestDeleteMyHeartItem,
} from '@apis/renewal/myHeart';
import { MolocoAdProducts } from '@wconcept/wconcept-common/models/display/search';
import { DisplayContentInfo } from '@wconcept/wconcept-common/models/renewal/display';
import { DisplayAreaContentsInfo } from '@wconcept/wconcept-common/models/display/display';
import { BrandInfo } from '@wconcept/wconcept-common/models/renewal/brand';
import { StyleclipContent } from '@wconcept/wconcept-common/models/renewal/styleclip';
import type { Product as AgentProduct } from '@models/agent/product';
import {
  BrandStory,
  DiscoveryItem,
  LookBook,
  StyleClip,
  WSeries,
} from '@wconcept/wconcept-common/models/renewal/discovery';
import {
  MyHeartBrandInfo,
  MyHeartMyPageContentTabContentItem,
} from '@wconcept/wconcept-common/models/renewal/myHeart';
import { ApiErrorClass } from '@utils/customErrors';
import { getByteplusPlatform } from '@wconcept/wconcept-common/utils/deviceUtil';
import { Brand, Event } from '@components/pages/shopPilot/types';
import useHistoryRouter from './useLinkTo';
import useGlobalState from './useGlobalState';
import useByteplusLogger from './useByteplusLogger';
import useAppsFlyerEvent from './useAppsFlyerEvent';
import useAirBridgeEvent from './useAirBridgeEvent';

interface IUseMyHeartItemProps {
  isLogin: boolean;
  doAfterAddMyHeart?: (
    param: MyHeartItemRequestParam,
    product: Product | MolocoAdProducts | DisplayProduct,
    index?: number,
    type?: string,
    area?: string,
    affiliation?: string,
    tabNm?: string
  ) => void;
  doAfterDeleteMyHeart?: (
    param: MyHeartItemRequestParam,
    product: Product | MolocoAdProducts | DisplayProduct,
    index?: number,
    type?: string,
    area?: string,
    affiliation?: string,
    tabNm?: string
  ) => void;
  doAfterAddBrandMyHeart?: (
    param: MyHeartBrandRequestParam,
    value:
      | DisplayContentInfo
      | DisplayAreaContentsInfo
      | MyHeartBrandInfo
      | BrandInfo
      | Brand,
    index?: number,
    area?: string,
    tabNm?: string
  ) => void;
  doAfterDeleteBrandMyHeart?: (
    param: MyHeartBrandRequestParam,
    value:
      | DisplayContentInfo
      | DisplayAreaContentsInfo
      | MyHeartBrandInfo
      | BrandInfo
      | Brand,
    index?: number,
    area?: string,
    tabNm?: string
  ) => void;
  doAfterAddEventMyHeart?: (
    param: MyHeartContentRequestParam,
    value: DisplayContentInfo,
    index?: number,
    type?: string,
    area?: string
  ) => void;
  doAfterDeleteEventMyHeart?: (
    param: MyHeartContentRequestParam,
    value: DisplayContentInfo,
    index?: number,
    type?: string,
    area?: string
  ) => void;
  doAfterAddBrandStoryMyHeart?: (
    param: MyHeartContentRequestParam,
    value: BrandStory,
    index?: number
  ) => void;
  doAfterDeleteBrandStoryMyHeart?: (
    param: MyHeartContentRequestParam,
    value: BrandStory,
    index?: number
  ) => void;
  doAfterAddLookBookMyHeart?: (
    param: MyHeartContentRequestParam,
    value: LookBook,
    index?: number
  ) => void;
  doAfterDeleteLookBookMyHeart?: (
    param: MyHeartContentRequestParam,
    value: LookBook,
    index?: number
  ) => void;
  doAfterAddWSeriesMyHeart?: (
    param: MyHeartContentRequestParam,
    value: WSeries,
    index?: number
  ) => void;
  doAfterDeleteWSeriesMyHeart?: (
    param: MyHeartContentRequestParam,
    value: WSeries,
    index?: number
  ) => void;
  doAfterAddStyleClipMyHeart?: (
    param: MyHeartContentRequestParam,
    value: StyleclipContent | StyleClip,
    index?: number
  ) => void;
  doAfterDeleteStyleClipMyHeart?: (
    param: MyHeartContentRequestParam,
    value: StyleclipContent | StyleClip,
    index?: number
  ) => void;
}

const useMyHeartItem = ({
  isLogin,
  doAfterAddMyHeart,
  doAfterDeleteMyHeart,
  doAfterAddBrandMyHeart,
  doAfterDeleteBrandMyHeart,
  doAfterAddEventMyHeart,
  doAfterDeleteEventMyHeart,
  doAfterAddBrandStoryMyHeart,
  doAfterDeleteBrandStoryMyHeart,
  doAfterAddLookBookMyHeart,
  doAfterDeleteLookBookMyHeart,
  doAfterAddWSeriesMyHeart,
  doAfterDeleteWSeriesMyHeart,
  doAfterAddStyleClipMyHeart,
  doAfterDeleteStyleClipMyHeart,
}: IUseMyHeartItemProps) => {
  const { linkTo } = useHistoryRouter();
  const { appInfo, assistData, isMobile } = useGlobalState();
  const { writeUserEvents } = useByteplusLogger();
  const { sendAppsFlyerMyHeartProduct, sendAppsFlyerMyHeartBrand } =
    useAppsFlyerEvent();
  const { sendAirBridgeMyHeartProduct, sendAirBridgeMyHeartBrand } =
    useAirBridgeEvent();
  const addMyHeart = useCallback(
    async (
      param: MyHeartItemRequestParam,
      product: Product | MolocoAdProducts,
      index?: number,
      type?: string,
      area?: string,
      affiliation?: string,
      tabNm?: string
    ) => {
      try {
        const res = await requestAddMyHeartItem(param);
        if (res && res === 'SUCCESS') {
          if (doAfterAddMyHeart) {
            doAfterAddMyHeart(
              param,
              product,
              index,
              type,
              area,
              affiliation,
              tabNm
            );

            // byteplus
            writeUserEvents({
              data: [
                {
                  user_id: assistData.user.userNo.toString(),
                  event_type: 'add-to-favorites',
                  event_timestamp: Math.floor(Date.now() / 1000),
                  product_id: product.itemCd,
                  offset: (index || 0) + 1,
                  platform: getByteplusPlatform(appInfo.isNativeAppBoth),
                  item_type: 'product',
                },
              ],
            });
          }
          // 마이하트 추가할때만 appsflyer/airbridge 이벤트 전송 (공통정책)
          sendAppsFlyerMyHeartProduct({
            product: product as Product,
            heratYn: true,
          });
          sendAirBridgeMyHeartProduct({
            product: product as Product,
            heratYn: true,
          });
        }
        return res;
      } catch (e) {
        console.error(`addMyHeart :: error : ${e}`);
        return null;
      }
    },
    [
      appInfo.isNativeAppBoth,
      assistData.user.userNo,
      doAfterAddMyHeart,
      writeUserEvents,
      sendAppsFlyerMyHeartProduct,
      sendAirBridgeMyHeartProduct,
    ]
  );

  const deleteMyHeart = useCallback(
    async (
      param: MyHeartItemRequestParam,
      product: Product,
      index?: number,
      type?: string,
      area?: string,
      affiliation?: string,
      tabNm?: string
    ) => {
      try {
        const res = await requestDeleteMyHeartItem(param);
        if (res && res === 'SUCCESS') {
          if (doAfterDeleteMyHeart)
            doAfterDeleteMyHeart(
              param,
              product,
              index,
              type,
              area,
              affiliation,
              tabNm
            );

          // byteplus
          writeUserEvents({
            data: [
              {
                user_id: assistData.user.userNo.toString(),
                event_type: 'remove-from-favorites',
                event_timestamp: Math.floor(Date.now() / 1000),
                product_id: product.itemCd,
                offset: (index || 0) + 1,
                platform: getByteplusPlatform(appInfo.isNativeAppBoth),
                item_type: 'product',
              },
            ],
          });
        }
        return res;
      } catch (e) {
        console.error(`deleteMyHeart :: error : ${e}`);
        return null;
      }
    },
    [
      appInfo.isNativeAppBoth,
      assistData.user.userNo,
      doAfterDeleteMyHeart,
      writeUserEvents,
    ]
  );

  const addBrandMyHeart = useCallback(
    async (
      param: MyHeartBrandRequestParam,
      value:
        | DisplayContentInfo
        | DisplayAreaContentsInfo
        | MyHeartBrandInfo
        | BrandInfo
        | Brand,
      index?: number,
      area?: string,
      tabNm?: string
    ) => {
      try {
        const res = await requestAddMyHeartBrand(param);
        if (res && res === 'SUCCESS') {
          if (doAfterAddBrandMyHeart)
            doAfterAddBrandMyHeart(param, value, index, area, tabNm);
          // 마이하트 추가할때만 appsflyer/airbridge 이벤트 전송 (공통정책)
          sendAppsFlyerMyHeartBrand({ value, heratYn: true });
          sendAirBridgeMyHeartBrand({ value, heratYn: true });
        }
        return res;
      } catch (e) {
        console.error(`addBrandMyHeart :: error : ${e}`);
        return null;
      }
    },
    [
      doAfterAddBrandMyHeart,
      sendAppsFlyerMyHeartBrand,
      sendAirBridgeMyHeartBrand,
    ]
  );

  const deleteBrandMyHeart = useCallback(
    async (
      param: MyHeartBrandRequestParam,
      value:
        | DisplayContentInfo
        | DisplayAreaContentsInfo
        | MyHeartBrandInfo
        | BrandInfo
        | Brand,
      index?: number,
      area?: string,
      tabNm?: string
    ) => {
      try {
        const res = await requestDeleteMyHeartBrand(param);
        if (res && res === 'SUCCESS') {
          if (doAfterDeleteBrandMyHeart)
            doAfterDeleteBrandMyHeart(param, value, index, area, tabNm);
        }
        return res;
      } catch (e) {
        console.error(`deleteMyHeart :: error : ${e}`);
        return null;
      }
    },
    [doAfterDeleteBrandMyHeart]
  );

  const addContentsMyHeart = useCallback(
    async (
      param: MyHeartContentRequestParam,
      value:
        | DisplayContentInfo
        | StyleclipContent
        | BrandStory
        | LookBook
        | StyleClip
        | Event,
      type: 'EVENT' | 'BRAND_STORY' | 'STYLE_CLIP' | 'W_SERIES' | 'LOOKBOOK',
      index?: number,
      area?: string
    ) => {
      try {
        const res = await requestAddMyHeartContents(param);
        if (res && res === 'SUCCESS') {
          // 앱스킴 전송
          const paramUrl = `heartType=CONTENT&state=ON&contentType=${type}&contentId=${value.id}`;
          if (appInfo?.isNativeAppBoth) {
            window.location.href = `wconceptkr://myHeart?${paramUrl}`;
          }
          switch (type) {
            case 'EVENT':
              if (doAfterAddEventMyHeart)
                doAfterAddEventMyHeart(
                  param,
                  value as DisplayContentInfo,
                  index,
                  type,
                  area
                );
              break;
            case 'BRAND_STORY':
              if (doAfterAddBrandStoryMyHeart) {
                doAfterAddBrandStoryMyHeart(param, value as BrandStory, index);
              }
              // byteplus
              writeUserEvents({
                data: [
                  {
                    user_id: assistData.user.userNo.toString(),
                    event_type: 'like',
                    event_timestamp: Math.floor(Date.now() / 1000),
                    page_number: 1,
                    offset: (index || 0) + 1,
                    platform: getByteplusPlatform(appInfo.isNativeAppBoth),
                    item_type: 'content',
                    content_id: `B${value.id}`,
                    product_id: `B${value.id}`,
                  },
                ],
              });
              break;
            case 'LOOKBOOK':
              if (doAfterAddLookBookMyHeart) {
                doAfterAddLookBookMyHeart(param, value as LookBook, index);
              }
              break;
            case 'W_SERIES':
              if (doAfterAddWSeriesMyHeart) {
                doAfterAddWSeriesMyHeart(param, value as WSeries, index);
              }
              // byteplus
              writeUserEvents({
                data: [
                  {
                    user_id: assistData.user.userNo.toString(),
                    event_type: 'like',
                    event_timestamp: Math.floor(Date.now() / 1000),
                    page_number: 1,
                    offset: (index || 0) + 1,
                    platform: getByteplusPlatform(appInfo.isNativeAppBoth),
                    item_type: 'content',
                    content_id: `W${value.id}`,
                    product_id: `W${value.id}`,
                  },
                ],
              });
              break;
            case 'STYLE_CLIP':
              if (doAfterAddStyleClipMyHeart) {
                doAfterAddStyleClipMyHeart(
                  param,
                  value as StyleclipContent | StyleClip,
                  index
                );
              }
              // byteplus
              writeUserEvents({
                data: [
                  {
                    user_id: assistData.user.userNo.toString(),
                    event_type: 'like',
                    event_timestamp: Math.floor(Date.now() / 1000),
                    page_number: 1,
                    offset: (index || 0) + 1,
                    platform: getByteplusPlatform(appInfo.isNativeAppBoth),
                    item_type: 'content',
                    content_id: `S${value.id}`,
                    product_id: `S${value.id}`,
                  },
                ],
              });
              break;
            default:
              break;
          }
        }
        return res;
      } catch (e) {
        console.error(`addContentsMyHeart :: error : ${e}`);
        const error = e as ApiErrorClass;
        if (error?.status === 30103) {
          return 'SUCCESS';
        }
        return null;
      }
    },
    [
      appInfo?.isNativeAppBoth,
      doAfterAddBrandStoryMyHeart,
      doAfterAddEventMyHeart,
      doAfterAddLookBookMyHeart,
      doAfterAddStyleClipMyHeart,
      doAfterAddWSeriesMyHeart,
    ]
  );

  const deleteContentsMyHeart = useCallback(
    async (
      param: MyHeartContentRequestParam,
      value:
        | DisplayContentInfo
        | StyleclipContent
        | BrandStory
        | LookBook
        | WSeries
        | StyleClip
        | Event,
      type: string,
      index?: number,
      area?: string
    ) => {
      try {
        const res = await requestDeleteMyHeartContents(param);
        if (res === 'SUCCESS') {
          const paramUrl = `heartType=CONTENT&state=OFF&contentType=${type}&contentId=${param.contentId}`;
          if (appInfo?.isNativeAppBoth) {
            window.location.href = `wconceptkr://myHeart?${paramUrl}`;
          }
          switch (type) {
            case 'EVENT':
              if (doAfterDeleteEventMyHeart)
                doAfterDeleteEventMyHeart(
                  param,
                  value as DisplayContentInfo,
                  index,
                  type,
                  area
                );
              break;
            case 'BRAND_STORY':
              if (doAfterDeleteBrandStoryMyHeart) {
                doAfterDeleteBrandStoryMyHeart(
                  param,
                  value as BrandStory,
                  index
                );
              }
              break;
            case 'LOOKBOOK':
              if (doAfterDeleteLookBookMyHeart) {
                doAfterDeleteLookBookMyHeart(param, value as LookBook, index);
              }
              break;
            case 'W_SERIES':
              if (doAfterDeleteWSeriesMyHeart) {
                doAfterDeleteWSeriesMyHeart(param, value as WSeries, index);
              }
              break;
            case 'STYLE_CLIP':
              if (doAfterDeleteStyleClipMyHeart) {
                doAfterDeleteStyleClipMyHeart(
                  param,
                  value as StyleclipContent | StyleClip,
                  index
                );
              }
              break;
            default:
              break;
          }
        }
        return res;
      } catch (e) {
        console.error(`deleteMyHeart :: error : ${e}`);
        return null;
      }
    },
    [
      appInfo?.isNativeAppBoth,
      doAfterDeleteEventMyHeart,
      doAfterDeleteBrandStoryMyHeart,
      doAfterDeleteLookBookMyHeart,
      doAfterDeleteWSeriesMyHeart,
      doAfterDeleteStyleClipMyHeart,
    ]
  );

  const loginValidation = useCallback(
    async (optionUrl?: string) => {
      if (
        window.confirm(
          '로그인이 필요한 서비스 입니다.\n로그인 페이지로 이동하시겠습니까?'
        )
      ) {
        const url = `${
          isMobile
            ? await addAsIsPcWebDomain('/Member/Login')
            : await addAsIsMWebDomain('/Member/Login')
        }?rUrl=${encodeURIComponent(optionUrl || currentUrl())}`;

        linkTo.push({
          url,
        });
      }
      return false;
    },
    [linkTo]
  );

  // EVENT HANDLER : 상품 찜 하기 클릭
  const onClickHeart = useCallback(
    async (
      product: Product,
      active: boolean,
      index?: number,
      type?: string,
      area?: string,
      affiliation?: string,
      tabNm?: string,
      targetUrl?: string
    ) => {
      if (!isLogin) {
        const shouldLogin = await loginValidation(targetUrl);
        return shouldLogin ? 'LOGIN_REQUIRED' : null;
      }
      const trackingNum = await getCookieTrackingIssueNo();
      const recentKeywords = await getWckRecentResultList();
      const param: MyHeartItemRequestParam = {
        itemCd: product.itemCd,
        brandCd: product.brandCd ? (product.brandCd as unknown as number) : 0,
        eventId: trackingNum,
        lastKeyword: recentKeywords.split(',')?.[0],
      };
      // 앱스킴 전송
      const paramUrl = `heartType=PRODUCT&state=${
        active ? 'OFF' : 'ON'
      }&productCd=${product.itemCd}`;

      if (appInfo?.isNativeAppBoth) {
        window.location.href = `wconceptkr://myHeart?${paramUrl}`;
      }
      if (!active) {
        return addMyHeart(
          param,
          product,
          index,
          type,
          area,
          affiliation,
          tabNm
        );
      }
      return deleteMyHeart(
        param,
        product,
        index,
        type,
        area,
        affiliation,
        tabNm
      );
    },
    [
      isLogin,
      deleteMyHeart,
      loginValidation,
      appInfo?.isNativeAppBoth,
      addMyHeart,
    ]
  );

  // EVENT HANDLER : 브랜드 찜 하기 클릭
  const onClickBrandHeart = useCallback(
    async (
      value:
        | DisplayContentInfo
        | DisplayAreaContentsInfo
        | MyHeartBrandInfo
        | BrandInfo
        | Brand,
      active: boolean,
      index?: number,
      area?: string,
      tabNm?: string
    ) => {
      if (!isLogin) {
        loginValidation();
        return null;
      }
      const param: MyHeartBrandRequestParam = {
        brandCd: value?.brandCd?.toString() || '',
      };
      // 앱스킴 전송
      const paramUrl = `heartType=BRAND&state=${
        active ? 'OFF' : 'ON'
      }&brandCd=${value.brandCd}`;

      if (appInfo?.isNativeAppBoth) {
        window.location.href = `wconceptkr://myHeart?${paramUrl}`;
      }
      if (!active) {
        return addBrandMyHeart(param, value, index, area, tabNm);
      }
      return deleteBrandMyHeart(param, value, index, area, tabNm);
    },
    [
      isLogin,
      appInfo?.isNativeAppBoth,
      deleteBrandMyHeart,
      loginValidation,
      addBrandMyHeart,
    ]
  );

  // EVENT HANDLER : 컨텐츠 - 기획전 찜 하기 클릭
  const onClickEventHeart = useCallback(
    async (
      value: DisplayContentInfo | Event,
      active: boolean,
      index?: number,
      area?: string
    ) => {
      if (!isLogin) {
        loginValidation();
        return null;
      }
      const user = await getCookieWckAuthData();
      const contentId =
        'eventId' in value
          ? value.eventId?.toString() || ''
          : value.id?.toString() || '';
      const param: MyHeartContentRequestParam = {
        custNo: user.userNo || '',
        contentType: 'EVENT',
        contentId,
      };

      if (!active) {
        return addContentsMyHeart(param, value, 'EVENT', index, area);
      }
      return deleteContentsMyHeart(param, value, 'EVENT', index, area);
    },
    [isLogin, deleteContentsMyHeart, loginValidation, addContentsMyHeart]
  );

  // EVENT HANDLER : 컨텐츠 - 스타일클립 찜 하기 클릭
  const onClickStyleClipHeart = useCallback(
    async (
      value: StyleclipContent | StyleClip,
      active: boolean,
      index?: number,
      optionUrl?: string
    ) => {
      if (!isLogin) {
        loginValidation(optionUrl);
        return null;
      }
      const user = await getCookieWckAuthData();
      const param: MyHeartContentRequestParam = {
        custNo: user.userNo || '',
        contentType: 'STYLE_CLIP',
        profileSeqNo: value?.profileInfo?.profile.profileSeqNo
          ? value?.profileInfo.profile.profileSeqNo
          : undefined,
        contentId: value.id?.toString() || '',
      };

      if (!active) {
        return addContentsMyHeart(param, value, 'STYLE_CLIP', index);
      }
      return deleteContentsMyHeart(param, value, 'STYLE_CLIP', index);
    },
    [isLogin, deleteContentsMyHeart, loginValidation, addContentsMyHeart]
  );

  // EVENT HANDLER : 컨텐츠 - 브래드스토리 찜 하기 클릭
  const onClickBrandStoryHeart = useCallback(
    async (
      value: DisplayContentInfo,
      active: boolean,
      index?: number,
      optionUrl?: string
    ) => {
      if (!isLogin) {
        await loginValidation(optionUrl);
        return null;
      }
      const user = await getCookieWckAuthData();
      const param: MyHeartContentRequestParam = {
        custNo: user.userNo || '',
        contentType: 'BRAND_STORY',
        contentId: value.id?.toString() || '',
      };

      if (!active) {
        return addContentsMyHeart(param, value, 'BRAND_STORY', index);
      }
      return deleteContentsMyHeart(param, value, 'BRAND_STORY', index);
    },
    [isLogin, deleteContentsMyHeart, loginValidation, addContentsMyHeart]
  );

  // EVENT HANDLER : 컨텐츠 - 룩북 찜 하기 클릭
  const onClickLookBookHeart = useCallback(
    async (value: LookBook, active: boolean, index?: number) => {
      if (!isLogin) {
        loginValidation();
        return null;
      }
      const user = await getCookieWckAuthData();
      const param: MyHeartContentRequestParam = {
        custNo: user.userNo || '',
        contentType: 'LOOKBOOK',
        contentId: value.id.toString() || '',
      };

      if (!active) {
        return addContentsMyHeart(param, value, 'LOOKBOOK', index);
      }
      return deleteContentsMyHeart(
        param,
        value as DisplayContentInfo,
        'LOOKBOOK',
        index
      );
    },
    [isLogin, loginValidation, addContentsMyHeart, deleteContentsMyHeart]
  );

  // EVENT HANDLER : 컨텐츠 - w시리즈 찜 하기 클릭
  const onClickWSeriesHeart = useCallback(
    async (
      value: WSeries,
      active: boolean,
      index?: number,
      optionUrl?: string
    ) => {
      if (!isLogin) {
        loginValidation(optionUrl);
        return null;
      }
      const user = await getCookieWckAuthData();
      const param: MyHeartContentRequestParam = {
        custNo: user.userNo || '',
        contentType: 'W_SERIES',
        contentId: value.id?.toString() || '',
      };

      if (!active) {
        return addContentsMyHeart(param, value, 'W_SERIES', index);
      }
      return deleteContentsMyHeart(param, value, 'W_SERIES', index);
    },
    [isLogin, deleteContentsMyHeart, loginValidation, addContentsMyHeart]
  );

  // EVENT HANDLER : 마이하트 컨텐츠 - 찜 하기 클릭
  const onClickMyHeartContentsHeart = useCallback(
    async (
      value: MyHeartMyPageContentTabContentItem,
      active: boolean,
      type: 'BRAND_STORY' | 'STYLE_CLIP' | 'W_SERIES' | 'LOOKBOOK' | 'EVENT',
      index?: number
    ) => {
      if (!isLogin) {
        loginValidation();
        return null;
      }
      const user = await getCookieWckAuthData();
      const param: MyHeartContentRequestParam = {
        custNo: user.userNo || '',
        contentType: type,
        contentId: value.contentId,
        profileSeqNo:
          value.profileSeqNo && type === 'STYLE_CLIP'
            ? value.profileSeqNo
            : undefined,
      };
      if (!active) {
        return addContentsMyHeart(param, value, type, index);
      }
      return deleteContentsMyHeart(param, value, type, index);
    },
    [isLogin, loginValidation, addContentsMyHeart, deleteContentsMyHeart]
  );

  return {
    onClickHeart,
    onClickBrandHeart,
    onClickEventHeart,
    onClickStyleClipHeart,
    onClickBrandStoryHeart,
    onClickLookBookHeart,
    onClickWSeriesHeart,
    onClickMyHeartContentsHeart,
    loginValidation,
  };
};

export default useMyHeartItem;
